/**
 * @var {number}
 * Defines how often the searcher will poll for new results.
 */
let searchPollingDefaultInterval = 10 * 1000;

/**
 * @var {Object<string, number|false>}
 * Determines how often the searcher of the given entity will poll for new results. If value is false, the searcher
 * page won't make poll requests.
 */
const searchPollingIntervalByEntity: { [k: string]: number | false } = {
  adMap: false,
  adPosition: false,
  socialNetworkAccount: false,
  socialNetworkApp: false,
  staff: false,
  role: false,
  user: false,
  binaryFile: false,
  digitalInterviewQuestion: false,
  pushNotification: false,
  auditEventEntity: false,
  auditEventProcessingResult: false,
  assistantRequestTrail: false,
  assistantMonthlyRequestTrail: false,
};

/**
 * Sets the default search-polling interval.
 * @param {number} defaultIntervalInSeconds Seconds to wait between polling requests. Must be greater than 5.
 */
export function setSearchPollingDefaultInterval(defaultIntervalInSeconds: number): void {
  if (defaultIntervalInSeconds <= 5) {
    throw new Error('Search polling interval must be greater than 5 seconds.');
  }
  searchPollingDefaultInterval = defaultIntervalInSeconds * 1000;
}

/**
 * Sets the search-polling interval for the given entity.
 * @param {string} entity
 * @param {number} intervalInSeconds Seconds to wait between polling requests. Must be defined and greater than 5.
 */
export function setSearchPollingIntervalForEntity(entity: string, intervalInSeconds?: number): void {
  if (!intervalInSeconds || intervalInSeconds < 5) {
    throw new Error('Search polling interval must be greater than 5 seconds.');
  } else {
    searchPollingIntervalByEntity[entity] = intervalInSeconds * 1000;
  }
}

/**
 * Disables the search-polling functionality for the given entity.
 * @param {string} entity
 */
export function disableSearchPollingForEntity(entity: string): void {
  searchPollingIntervalByEntity[entity] = false;
}

/**
 * Removes the custom search-polling interval configured for the given entity, if any.
 * @param {string} entity
 */
export function unsetSearchPollingIntervalForEntity(entity: string): void {
  delete searchPollingIntervalByEntity[entity];
}

/**
 * Enables the search-polling functionality for the given entity.
 * @inheritDoc
 */
export const enableSearchPollingIntervalForEntity = unsetSearchPollingIntervalForEntity;

/**
 * Returns the polling interval defined for the given entity, in milliseconds.
 * @param {string} entity The entity being searched for.
 */
export function getSearchPollingIntervalForEntity(entity: string): number | false {
  if (searchPollingIntervalByEntity[entity] !== undefined) {
    return searchPollingIntervalByEntity[entity];
  }
  return searchPollingDefaultInterval;
}
